import React, { useEffect, useState } from 'react';
import { array, bool, func, object, string } from 'prop-types';

import { connect } from 'react-redux';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';

import EsmsDialog from '../../components/EsmsDialog';

import * as ApplicationMessageActions from '../../actions/applicationMessageActions';
import * as UiActions from '../../actions/uiActions';

import classes from './InviteUserModal.module.css';

const ValidationSchema = Yup.object().shape({
    email: Yup.string()
        .email('Must be an email address')
        .required(),
    role: Yup.string()
        .required()
});

const InviteUserModal = ({
    close,
    clearErrors,
    data,
    open,
    title,
    isLoading,
    toggleLoading,
    errors,
    ...props
}) => {
    const [availableRoles, setAvailableRoles] = useState([]);

    useEffect(() => {
        const { applications, roles } = data;
        if (applications && roles) {
            const hasTimeApp = applications.find(app => app.applicationName.toLowerCase() === 'time');
            if (hasTimeApp) {
                setAvailableRoles(roles);
            }
            else {
                const roleList = roles.filter(r => r.name.toLowerCase() !== 'timeappuser');
                setAvailableRoles(roleList);
            }
        }
        else {
            setAvailableRoles(data.roles);
        }
    }, [data]);

    const formik = useFormik({
        initialValues: {
            email: '',
            role:  ''
        },
        validationSchema:   ValidationSchema,
        enableReinitialize: true,
        onSubmit:           async (values, { setSubmitting }) => {
            const {
                create
            } = props;

            let isCreated;

            clearErrors();
            setSubmitting(true);

            try {
                if (!isLoading) {
                    toggleLoading(true);
                    isCreated = create(values);
                }

                if (isCreated) {
                    handleClose();
                } else {
                    setSubmitting(false);
                    toggleLoading(false);
                }
            } catch (error) {
                setSubmitting(false);
            }
        }
    });

    const handleClose = () => {
        toggleLoading(false);
        clearErrors();

        close();
    };

    return (
        <EsmsDialog
            onClose={close}
            disabled={formik.isSubmitting}
            onConfirm={formik.handleSubmit}
            confirmText="Invite"
            open={open}
            isLoading={isLoading}
            title={title}
            cancelable={true}
            errors={errors}
            onRendered={formik.handleReset}
        >
            <div className={classes.formColumn}>
                <div className={classes.labelColumn}>
                    <div className={classes.labelHeader}>
                        <img src="/assets/images/usersFlat.svg" alt="Users Icon" />
                        <span>Invite User</span>
                    </div>
                    <div className={classes.labelContent}>
                        <p>
                            Provide a valid email address. The invitation will be sent to this address.
                        </p>
                        <p>
                            Select a role for the invited user.
                        </p>
                    </div>
                </div>
                <div className={classes.inputColumn}>
                    <div className={classes.inputs}>
                        <FormControl className={classes.fullWidth}>
                            <TextField
                                id="email"
                                name="email"
                                type="text"
                                value={formik.values.email}
                                label="Email"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                error={Boolean(formik.errors.email) && Boolean(formik.touched.email)}
                                helperText={Boolean(formik.touched.email) && formik.errors.email}
                            />
                        </FormControl>
                        <FormControl className={classes.fullWidth}>
                            <InputLabel
                                htmlFor="role"
                                classes={{
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }}
                                error={Boolean(formik.errors.role) && Boolean(formik.touched.role)}
                            >
                                Role
                            </InputLabel>
                            <Select
                                value={formik.values.role}
                                name="role"
                                id="role"
                                onChange={formik.handleChange}
                                classes={{
                                    root: classes.select
                                }}
                                inputProps={{
                                    onBlur: formik.handleBlur
                                }}
                                MenuProps={{
                                    className: 'usersRoleMenu'
                                }}
                                error={Boolean(formik.errors.role) && Boolean(formik.touched.role)}
                            >
                                {availableRoles.map(role => {
                                    return (
                                        <MenuItem
                                            className={classes.selectMenuItem}
                                            key={role.id}
                                            value={role.id}
                                        >{role.name}</MenuItem>
                                    );
                                })}
                            </Select>
                            {formik.touched.role &&
                             formik.errors.role && <FormHelperText className={classes.error}>{formik.errors.role}</FormHelperText>}
                        </FormControl>
                    </div>
                </div>
            </div>
        </EsmsDialog>
    );
};

InviteUserModal.defaultProps = {
    clearErrors: () => {},
    close:       () => {},
    create:      () => {},
    data:        {},
    errors:      [],
    isLoading:   false,
    open:        false,
    title:       ''
};

InviteUserModal.propTypes = {
    clearErrors: func,
    close:       func,
    create:      func,
    data:        object,
    errors:      array,
    isLoading:   bool,
    open:        bool,
    title:       string
};

const mapStateToProps = state => {
    return {
        errors:    state.messages.errors,
        isLoading: state.ui.isLoading
    };
};

const mapDispatchToProps = {
    ...ApplicationMessageActions,
    ...UiActions
};

export default connect(mapStateToProps, mapDispatchToProps)(InviteUserModal);
